/**
 * InvitationListItem Component
 * Compact invitation row with colored avatar, email, role dropdown, and trash button
 */

import type { CSSProperties } from 'react';
import { memo, useCallback } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAvatarColor, getEmailInitials } from '@/constants/roles';
import { ICON_SIZE_SM } from '@/constants/ui';
import { RoleSelector, StatusBadge } from '@/components/common';
import { INVITABLE_ROLES } from '@/constants/workspace';
import type { WorkspaceRole } from '@/types/workspace';
import type { InvitationListItemProps } from '../types';
import styles from './InvitationListItem.module.css';

export const InvitationListItem = memo(function InvitationListItem({
  invitation,
  onRemove,
  onRoleChange,
  disabled = false,
}: InvitationListItemProps) {
  const { t } = useTranslation();

  const initials = getEmailInitials(invitation.email);
  const avatarColor = getAvatarColor(invitation.email);

  const handleRemove = useCallback(() => {
    onRemove(invitation.id);
  }, [invitation.id, onRemove]);

  const handleRoleChange = useCallback(
    (newRole: WorkspaceRole) => {
      onRoleChange(invitation.id, newRole);
    },
    [invitation.id, onRoleChange]
  );

  return (
    <div className={styles.listItem}>
      <div
        className={styles.avatar}
        style={{ '--avatar-color': avatarColor } as CSSProperties}
      >
        {initials}
      </div>
      <div className={styles.email}>{invitation.email}</div>
      <span className={styles.roleBadgeDefault}>
        <StatusBadge type="role" value={invitation.role} size="sm" />
      </span>
      <div className={styles.hoverControls}>
        <RoleSelector
          value={invitation.role}
          onChange={handleRoleChange}
          availableRoles={INVITABLE_ROLES}
          variant="badge"
          size="sm"
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.removeButton}
          onClick={handleRemove}
          disabled={disabled}
          aria-label={t('common.delete')}
        >
          <IconTrash size={ICON_SIZE_SM} />
        </button>
      </div>
    </div>
  );
});
